import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/translations';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import axios from 'axios';
import MasonryGallery from '@/components/MasonryGallery';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Ritratti = () => {
  const { language } = useLanguage();
  const [portfolioImages, setPortfolioImages] = useState([]);

  useEffect(() => {
    fetchPortfolioImages();
  }, []);

  const fetchPortfolioImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      const portraitsImages = response.data
        .filter(img => img.category === 'portraits')
        .sort((a, b) => a.order - b.order);
      setPortfolioImages(portraitsImages);
    } catch (error) {
      console.error('Error fetching portfolio images:', error);
    }
  };

  const faqs = [
    {
      question: t('portraits.faq1Q', language),
      answer: t('portraits.faq1A', language)
    },
    {
      question: t('portraits.faq2Q', language),
      answer: t('portraits.faq2A', language)
    },
    {
      question: t('portraits.faq3Q', language),
      answer: t('portraits.faq3A', language)
    },
    {
      question: t('portraits.faq4Q', language),
      answer: t('portraits.faq4A', language)
    },
    {
      question: t('portraits.faq5Q', language),
      answer: t('portraits.faq5A', language)
    }
  ];

  return (
    <div data-testid="ritratti-page">
      {/* Hero Section - Editorial style */}
      <section className="pt-32 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
                {t('portraits.title', language)}
              </p>
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-tight mb-6"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
                data-testid="hero-title"
              >
                {t('portraits.subtitle', language)}
              </h1>
              <p className="text-lg leading-relaxed text-muted-foreground mb-8 max-w-xl">
                {t('portraits.heroSubtitle', language)}
              </p>
              <Link to="/contatti">
                <Button size="lg" data-testid="cta-button">
                  {t('portraits.cta', language)}
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-2"
            >
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/w5tilu67_IMG_6649.jpg"
                alt="Matteo Giacometti in studio"
                className="w-full h-auto object-cover rounded-sm"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-secondary/20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-lg leading-relaxed text-muted-foreground"
          >
            {t('portraits.intro', language)}
          </motion.p>
        </div>
      </section>

      {/* Service Types */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
              {language === 'it' ? 'Servizi' : 'Services'}
            </p>
            <h2
              className="text-4xl sm:text-5xl font-light tracking-tight leading-tight"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t('portraits.servicesTitle', language)}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {[
              { title: t('portraits.service1Title', language), text: t('portraits.service1Text', language) },
              { title: t('portraits.service2Title', language), text: t('portraits.service2Text', language) },
              { title: t('portraits.service3Title', language), text: t('portraits.service3Text', language) },
              { title: t('portraits.service4Title', language), text: t('portraits.service4Text', language) }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="bg-secondary/10 p-8 rounded-sm"
              >
                <h3
                  className="text-2xl sm:text-3xl font-normal mb-4"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {service.title}
                </h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {service.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Gallery - Masonry Layout */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
              {language === 'it' ? 'Lavori' : 'Works'}
            </p>
            <h2
              className="text-4xl sm:text-5xl font-light tracking-tight leading-tight"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t('portraits.portfolioTitle', language)}
            </h2>
          </motion.div>

          {portfolioImages.length > 0 ? (
            <MasonryGallery images={portfolioImages} language={language} />
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">
                {language === 'it' 
                  ? 'Nessuna immagine disponibile. Carica le tue foto dal pannello Admin.' 
                  : 'No images available. Upload your photos from the Admin panel.'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className="text-4xl sm:text-5xl font-light tracking-tight leading-tight mb-8"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t('portraits.ctaTitle', language)}
            </h2>
            <Link to="/contatti">
              <Button size="lg" data-testid="final-cta-button">
                {t('portraits.ctaButton', language)}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Ritratti;
